'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay/classic');
const get = require('lodash.get');
const classNames = require('classnames');
const tokenReplace = require('dibs-token-replace');
const User = require('../../../global/models/User');
const ContextComponent = require('../../../global/components/ContextComponent');
const ContactDealerMessage = require('./ContactDealerMessage').default;
const ContactDealerMessageSent = require('./ContactDealerMessageSent').default;
const ContactDealerSidebarOutsideSales = require('./ContactDealerSidebarOutsideSales');
const RequestMoreInfoLinks = require('../../../contact/dealer/components/RequestMoreInfoLinks');
const phoneNumberCheck = require('dibs-phone-number-check');
const { trackContactDealer, trackCheckoutRedirect } = require('../../actions/pdpTrackingActions');

const contactDealerActions = require('../../actions/contactDealerActions.es');
const copyText = require('copy-text')().extend(
    require('../../../../../json/copy/contact-dealer.json')
);
const { shouldShowHoldRequest } = require('../../helpers/holdRequest.es');
const HoldRequestMutation = require('./HoldRequestMutation').default;
const AddProcurementItemMutation = require('./AddProcurementItemMutation').default;
const DealerDistinctions = require('./DealerDistinctions/DealerDistinctions').default;

// Helper functions
function makeLocation({ city, state, country }) {
    return `${city}, ${state || country}`;
}

function locationCopy({ copy, city, state, country }) {
    return tokenReplace(get(copy, 'dealer.location'), {
        location: makeLocation({ city, state, country }),
    });
}

function formatCityStateZip({ city, state, zip }) {
    return [[city, 'addressLocality'], [state, 'addressRegion'], [zip, 'postalCode']]
        .reduce((acc, item) => {
            const [value = '', key = ''] = item;

            if (typeof value === 'string') {
                acc.push(`<span data-tn="dealer-more-info-${key}">${value.trim()}</span>`);
            }

            return acc;
        }, [])
        .join('<span>,&nbsp;</span>');
}

/**
 * @param {string} dealerReferenceTitle - Title-text for dealer reference number.
 * @param {string} dealerReferenceNumber - Item dealer reference number.
 */
function dealerReferenceCopy({ dealerReferenceTitle, dealerReferenceNumber }) {
    if (dealerReferenceNumber && dealerReferenceTitle) {
        return (
            <span>
                {dealerReferenceTitle}: {dealerReferenceNumber}
            </span>
        );
    }
    return null;
}

dealerReferenceCopy.propTypes = {
    dealerReferenceTitle: PropTypes.string,
    dealerReferenceNumber: PropTypes.string,
};

const DEALER_TYPE_GALLERY = 'gallery';
const DEALER_TYPE_DEALER = 'dealer';
const DEALER_TYPE_SELLER = 'seller';

class ContactDealerBody extends ContextComponent {
    constructor(props) {
        super(props);

        const {
            city,
            state,
            country,
            zip,
            type,
            sinceYear,
            dealerReferenceNumber,
            dealerReferenceTitle,
        } = props.dealerData;
        const copy = props.copy;
        this.state = {
            nameError: false,
            messageError: false,
            phoneError: false,
            callDealerIsOpened: false,
            isHoldRequestChecked: false,
        };
        this._cityStateZip = formatCityStateZip({ copy, city, state, zip });
        this._locationCopy = locationCopy({ copy, city, state, country });
        this._sinceYearCopy = tokenReplace(get(copy, 'dealer.sinceYear'), { type, sinceYear });
        this._dealerReferenceCopy = dealerReferenceCopy({
            dealerReferenceTitle,
            dealerReferenceNumber,
        });
        this.onContactDealer = this.onContactDealer.bind(this);
        this.onMessageFocused = this.onMessageFocused.bind(this);
        this.onMessageEntered = this.onMessageEntered.bind(this);
        this.onMessageInput = this.onMessageInput.bind(this);
        this.onNameEntered = this.onNameEntered.bind(this);
        this.onHoldRequestClick = this.onHoldRequestClick.bind(this);
        this.onSubmitMessageLoggedIn = this.onSubmitMessageLoggedIn.bind(this);
    }
    componentDidUpdate(prevProps) {
        const { isDealerMessageSent, isPrintDealer } = this.props;

        if (isDealerMessageSent && !prevProps.isDealerMessageSent && !isPrintDealer) {
            this.context.executeAction(trackContactDealer, { action: 'submitted' });
        }
    }
    onSubmitMessageLoggedIn() {
        this.context.executeAction(
            contactDealerActions.submitMessageLoggedIn,
            User.getCurrentUser().toJSON()
        );
    }
    onContactDealer() {
        const {
            dealerData: { company },
            errors,
            backboneItem: item,
            offer,
            offerAmount,
            price,
            userDisplayName,
            userMessage,
            userId,
            itemId,
            vertical,
            relay,
            isRemoveContactDealerCTATestRunning,
        } = this.props;
        const hasDisplay = User.loggedIn() || userDisplayName;
        const trackingObj = {
            action: 'send clicked',
        };
        if (!hasDisplay || !userMessage) {
            this.setState({
                messageError: !userMessage,
                nameError: !userDisplayName,
            });
        } else if (offerAmount && !errors.length) {
            this.context.executeAction(trackCheckoutRedirect, { offerAmount, price });
            this.context.executeAction(contactDealerActions.submitOffer, { item, offer });
        } else if (User.loggedIn()) {
            if (isRemoveContactDealerCTATestRunning) {
                this.context.executeAction(contactDealerActions.sendMessageBegin);
                AddProcurementItemMutation.commit(this.props.relay.environment, {
                    userId,
                    itemIds: [itemId],
                    note: this.state.userMessage,
                    skipHoldRequest: true,
                    onCompleted: () =>
                        this.context.executeAction(contactDealerActions.sendMessageEnd),
                });
            } else if (this.state.isHoldRequestChecked) {
                relay.commitUpdate(
                    new HoldRequestMutation({
                        userId,
                        itemId,
                        sellerId: this.props.item.seller.serviceId,
                        vertical,
                    }),
                    {
                        onSuccess: () => this.onSubmitMessageLoggedIn(),
                    }
                );
            } else {
                this.onSubmitMessageLoggedIn();
            }
            this.context.executeAction(trackContactDealer, trackingObj);
        } else {
            this.context.executeAction(contactDealerActions.submitMessageNotLoggedIn, { company });
            this.context.executeAction(trackContactDealer, trackingObj);
        }
    }
    onMessageEntered(e) {
        const { value } = e.target;
        this.setState({ messageError: !value });
        this.context.executeAction(contactDealerActions.enterMessage, { value });
    }
    onMessageInput(event) {
        const { value: dealerMessage } = event.target;
        this.setState({ phoneError: phoneNumberCheck(dealerMessage) });
    }
    onMessageFocused() {
        const { hasTrackingFired } = this.props;
        if (!hasTrackingFired) {
            this.context.executeAction(trackContactDealer, { action: 'clicked' });
        }
    }
    onNameEntered(e) {
        const { value } = e.target;
        this.setState({ nameError: !value });
        this.context.executeAction(contactDealerActions.enterName, { value });
    }
    _makeAssociationList(associations = []) {
        const associationsCopy = get(this.props, 'copy.dealer.associations');

        if (Array.isArray(associations)) {
            const elements = associations.reduce((memo, association, index) => {
                association = get(association, 'displayName');
                memo.push(<li key={index}>{association}</li>);
                return memo;
            }, []);

            const associationsContainer = (
                <div className="pdp-dealer-body-associations">
                    {`${associationsCopy}:`}
                    <ul>{elements}</ul>
                </div>
            );

            return associations.length > 0 ? (
                <div className="pdp-dealer-company-info">{associationsContainer}</div>
            ) : null;
        } else {
            return null;
        }
    }
    onHoldRequestClick(checked) {
        this.setState({ isHoldRequestChecked: checked });
    }
    render() {
        const {
            contactDealerCTATestRunning,
            isRemoveContactDealerCTATestRunning,
            copy,
            handleContactDealerCtaClick,
            isDealerMessageSending,
            isDealerMessageSent,
            isPrintDealer,
            item,
            itemId,
            relay,
            user,
            userId,
            viewer,
            dealerData: { pk, uri, association },
            sellerResponseTime,
        } = this.props;

        const { hasUserId, userIds } = relay.variables;
        const { nameError, messageError, phoneError, isHoldRequestChecked } = this.state;

        const detailDataTags = isPrintDealer ? 'print-item-details-dealer' : 'item-details-dealer';
        const seller = get(item, 'seller');
        const bodyClasses = classNames('pdp-dealer-body');
        const dealerInfoClasses = classNames('pdp-dealer-info', 'pdp-dealer-section');
        const canShowSellerName = get(user, 'canShowSellerName');
        const sellerName = get(item, 'seller.sellerProfile.company');

        let dealerInfoBlock = null;
        if (isRemoveContactDealerCTATestRunning) {
            dealerInfoBlock = (
                <ContactDealerSidebarOutsideSales
                    wrapperClass={dealerInfoClasses}
                    copyText={copyText}
                    viewer={viewer}
                    userIds={userIds}
                    hasUserId={hasUserId}
                />
            );
        } else {
            dealerInfoBlock = (
                <div className={dealerInfoClasses} data-tn="pdp-dealer-info">
                    <div className="pdp-dealer-heading">
                        <div className="pdp-contact-dealer-title header-primary-large pdp-secondary-header">
                            {canShowSellerName ? sellerName : get(copy, 'dealer.aboutTheSeller')}
                        </div>
                    </div>
                    {seller && <DealerDistinctions seller={seller} />}
                    <p className="pdp-dealer-company-info">{this._sinceYearCopy}</p>
                    {sellerResponseTime && (
                        <p className="pdp-dealer-company-info">
                            {get(copy, 'dealer.typicalResponseTime')}: {sellerResponseTime}
                        </p>
                    )}
                    <p className="pdp-dealer-company-info">{this._locationCopy}</p>
                    {this._makeAssociationList(association ? association.slice(0, 3) : null)}
                </div>
            );
        }

        return (
            <div className={bodyClasses} data-tn={detailDataTags}>
                {dealerInfoBlock}
                {isDealerMessageSent ? (
                    <ContactDealerMessageSent
                        isRemoveContactDealerCTATestRunning={isRemoveContactDealerCTATestRunning}
                    />
                ) : (
                    <ContactDealerMessage
                        isDealerMessageSending={isDealerMessageSending}
                        isLoggedIn={User.loggedIn()}
                        messageError={messageError}
                        nameError={nameError}
                        phoneError={phoneError}
                        onContactDealer={this.onContactDealer}
                        onMessageEntered={this.onMessageEntered}
                        onMessageFocused={this.onMessageFocused}
                        onMessageInput={this.onMessageInput}
                        onNameEntered={this.onNameEntered}
                        showRequestHold={shouldShowHoldRequest(item, user)}
                        isHoldRequestChecked={isHoldRequestChecked}
                        onHoldRequestClick={this.onHoldRequestClick}
                        isRemoveContactDealerCTATestRunning={isRemoveContactDealerCTATestRunning}
                        userId={userId}
                        hasUserId={hasUserId}
                        itemId={itemId}
                        viewer={viewer}
                        dealerPk={pk}
                        page={uri}
                        item={item}
                    />
                )}
                {contactDealerCTATestRunning &&
                    user &&
                    user.isVerifiedTrade && (
                        <div className="pdp-dealer-request-more-info">
                            <RequestMoreInfoLinks
                                hasUserId={hasUserId}
                                userId={userId}
                                itemId={itemId}
                                user={user}
                                item={item}
                                handleCtaClick={handleContactDealerCtaClick}
                            />
                        </div>
                    )}
            </div>
        );
    }
}

ContactDealerBody.propTypes = {
    contactDealerCTATestRunning: PropTypes.bool.isRequired,
    isRemoveContactDealerCTATestRunning: PropTypes.bool.isRequired,
    canonicalUrl: PropTypes.string.isRequired,
    copy: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    favoriteDealer: PropTypes.object.isRequired,
    favoriteDealerCta: PropTypes.object,
    itemId: PropTypes.string.isRequired,
    itemPk: PropTypes.string.isRequired,
    offerAmount: PropTypes.string,
    price: PropTypes.number,
    vertical: PropTypes.string.isRequired,
    handleContactDealerCtaClick: PropTypes.func.isRequired,
    dealerData: PropTypes.shape({
        uri: PropTypes.string.isRequired,
        company: PropTypes.string.isRequired,
        sinceYear: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        state: PropTypes.string,
        zip: PropTypes.string,
        pk: PropTypes.string.isRequired,
        type: PropTypes.oneOf([DEALER_TYPE_GALLERY, DEALER_TYPE_DEALER, DEALER_TYPE_SELLER])
            .isRequired,
        association: PropTypes.array,
    }).isRequired,
    sellerResponseTime: PropTypes.string,
};

module.exports = Relay.createContainer(ContactDealerBody, {
    initialVariables: {
        hasUserId: false,
        userId: null,
        userIds: [],
        itemId: null,
    },
    fragments: {
        viewer: ({ hasUserId, userId, userIds, itemId }) => Relay.QL`
            fragment on Viewer {
                ${ContactDealerSidebarOutsideSales.getFragment('viewer', { hasUserId, userIds })}
                ${ContactDealerMessage.getFragment('viewer', { hasUserId, userId, itemId })}
            }
        `,
        user: ({ hasUserId, userId, itemId }) => Relay.QL`
            fragment on User {
                ${RequestMoreInfoLinks.getFragment('user', { hasUserId, userId, itemId })}
                isVerifiedTrade
                canUseNetPrice: hasPermission(name: "B_NET_PRICE")
                canShowSellerName
            }
        `,
        item: ({ hasUserId, userId, itemId }) => Relay.QL`
            fragment on Item {
                ${RequestMoreInfoLinks.getFragment('item', { hasUserId, userId, itemId })}
                isFurniture
                hasRequestedHoldForBuyer(buyerId: $userId) @include(if: $hasUserId)
                isPriceUponRequest
                isOnHold
                isSold
                attributes {
                    customizable {
                        value
                    }
                }
                seller {
                    serviceId
                    trackedPhone {
                        phoneNumber
                    }
                    sellerProfile {
                        company
                    }
                    ${DealerDistinctions.getFragment('seller')}
                }
                ${ContactDealerMessage.getFragment('item', { hasUserId, userId, itemId })}
            }
        `,
    },
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/ContactDealer/ContactDealerBody.jsx